import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { IDetailedWarning, IGetBookingsAirResponse } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { IGetEnumsService } from 'libraries/service/booking/enums/IGetEnums';
import {
    ISectorServicesConfig
} from 'libraries/component/booking-information/components/sectors/components/shared/interfaces/ISectorServicesConfig';
import { AirBookingResourceService } from 'libraries/service/resources/air-booking/air-booking-resource.service';
import {
    ITableServices,
    TableServicesService
} from 'libraries/component/booking-information/components/sectors/components/shared/services/table-services/table-services.service';
import { IBreadcrumbItem } from 'libraries/service/breadcrumb/IBreadcrumbItem';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { BreadcrumbIqbaService } from 'app_locobook/service/breadcrumbIqba/breadcrumbIqba.service';
import { Yii } from 'libraries/interfaces/Yii';
import { Sections } from 'libraries/component/booking-information/booking-information.component';
import { wenji } from 'libraries/interfaces/IWenji';
import { ExternalRedirectionService } from 'app_locobook/service/external-redirection.service';
import { IBookingExternalLink, IPostAirSearchRequest } from 'libraries/interfaces/i-post-air-search-request';
import { LabelService } from 'libraries/service/label/label.service';
import {
    CreateBookingWarningMessageService,
    IBookingErrorPriceChangedResponse,
    IBookingErrorPriceChangedResponsePayment
} from 'app_locobook/service/create-booking-warning-message.service';
import { IqbaPathSegment } from 'app_locobook/iqba-paths';
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import { EAirBookingPermissions } from "libraries/component/booking-information/e-air-booking-permissions.enum";
import { BookingAirToolsService } from "libraries/component/booking-information/services/booking-air-tools.service";
import { BookingCbCacheService } from "app_locobook/service/booking-cb-cache.service";
import {
    IAirCreateBookingRequest,
    IAirCreateBookingRequestPaymentPriceCostCurrency
} from "libraries/service/resources/model/booking/air/IAirCreateBooking";
import { GetChangedPriceWarningPipe } from "app_locobook/pages/air-booking-details/get-changed-price-warning.pipe";
import { PNRService } from "app_locobook/pnr/pnr.service";
import { ILoader } from "libraries/component/loader/ILoader";
import { LoaderType } from "libraries/component/loader/loader.component";
import { IAirDisplay } from "app_locobook/pnr/IAirDisplay";
import { PropalService } from "app_locobook/service/propal/propal.service";
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";
import { PriceSwitcherService } from "libraries/component/tab-navigation/price-switcher.service";
import { EXRateType } from "libraries/interfaces/ITotal";

class Wording {
    public readonly priceHasChanged: string = Yii.t('wui-shared-js', 'Le prix a changé.');
    public readonly resumeWithNewPrice: string = Yii.t('wui-shared-js', 'Continuer avec le nouveau prix');
    public readonly otherQuote: string = Yii.t('wui-shared-js', 'Choisir une autre proposition');
    public readonly loaderMessage: string = Yii.t('wui-shared-js', "Création de dossier en cours...");
    public readonly oldPrice: string = Yii.t('wui-shared-js', "Ancien prix : ");
    public readonly newPrice: string = Yii.t('wui-shared-js', "Nouveau prix : ");
}

@Component({
    selector: 'air-booking-details',
    template: `
        <div class="container">
            <notification *ngIf="notification.active"
                          [type]="notification.type">{{ notification.message }}</notification>
            <breadcrumb *ngIf="bookingData && breadcrumbItems.length"
                        [items]="breadcrumbItems"
                        (notify)="updateBreadcrumb($event)"></breadcrumb>

            <notification *ngIf="warningMessage" [type]="notificationType.Warning">
                {{warningMessage}}

                <div *ngIf="warningArray | getChangedPriceWarning" class="warning-action">
                    <div class="warning-action-message">
                        <div>
                            {{wording.priceHasChanged}}
                        </div>
                        <div *ngIf="notificationRequestPrice" class="notification-price">
                            <span>{{wording.oldPrice}}</span>
                            <price-real-amount [value]="notificationRequestPrice.amount"
                                               [currency]="notificationRequestPrice.currencyCode"></price-real-amount>

                        </div>
                        <div *ngIf="notificationNewPrice" class="notification-price">
                            <span>{{wording.newPrice}}</span>
                            <price-real-amount [value]="notificationNewPrice.amount"
                                               [currency]="notificationNewPrice.currencyCode || notificationNewPrice.currency"></price-real-amount>
                        </div>
                    </div>

                    <div class="warning-action-buttons">
                        <btn type="actionPrincipal"
                             [value]="wording.otherQuote"
                             (onClick)="chooseOtherQuote()"></btn>
                        <btn type="actionPrincipal"
                             [value]="wording.resumeWithNewPrice"
                             (onClick)="createBookingWithNewPrice()"></btn>
                    </div>
                </div>
            </notification>

            <loader *ngIf="loader.active" type="large" [message]="loader.message"></loader>

            <permission [path]="airBookingPermissions.SHARED_AIR_BOOKING_SHOW">
                <booking-information class="has-permission"
                                     [showBookingActions]="false"
                                     [showServicesAndOperations]="false"
                                     [sections]="sections"
                                     [serviceEnum]="serviceEnum"
                                     [bookingData]="bookingData"
                                     [bookingWithSelection]="bookingWithSelection"
                                     [tableServices]="tableServices"
                                     [isLoading]="isLoading"
                                     [isLoadingServices]="isLoadingServices"
                                     [isLoadingServicesError]="isLoadingServicesError"
                                     [isEditable]="isEditable">
                    <ng-container class="booking-information-hook">
                        <btn *ngIf="bookingExternalLink?.button?.show"
                             class="redirect-button"
                             type="actionDo"
                             [label]="bookingExternalLink?.button?.text"
                             (click)="redirectTo()"></btn>
                    </ng-container>
                </booking-information>

                <div class="has-no-permission">
                    <notification *ngIf="hiddenBookingNotification.active"
                                  [type]="hiddenBookingNotification.type">
                        {{ hiddenBookingNotification.message }}
                    </notification>
                </div>
            </permission>

        </div>
    `,
    styles: [`
        .container {
            margin: calc(50px + .75rem) .75rem .75rem .75rem;
        }

        .redirect-button {
            margin-right: 1rem;
        }

        notification {
            margin-bottom: .75rem;
        }

        .warning-action-message {
            margin: .75rem 0;
        }
        
        .notification-price {
            margin-left: 0.75rem;
            display: flex;
            width: 180px;
            justify-content: space-between;
        }
        .warning-action-buttons btn{
            margin-right: .75rem;
        }
    `]
})
export class AirBookingDetailsComponent implements OnInit {
    public bookingData: IGetBookingsAirResponse;
    // tslint:disable-next-line:no-any
    public bookingWithSelection: any;
    public isLoadingServices: boolean;
    public isLoadingServicesError: INotification = {
        active: false,
        type: NotificationType.Error,
    };

    public tableServices: ITableServices;
    public breadcrumbItems: IBreadcrumbItem[] = [];
    public notification: INotification = {
        active: false,
    };
    public hiddenBookingNotification: INotification = {
        active: true,
    };
    public serviceEnum: IGetEnumsService[];
    public servicesConfig: ISectorServicesConfig;

    public readonly loaderLabel: string = Yii.t('wui-iqba-js', 'Chargement du booking en cours');
    public readonly notificationType: typeof NotificationType = NotificationType;

    public isLoading: boolean = false;
    public isEditable: boolean = false;
    public sections: { [key in Sections]: boolean } = {
        [Sections.qualityCheckResult]: false
    };

    public bookingExternalLink: IBookingExternalLink = null;

    public warningMessage: string;
    public airBookingPermissions: typeof EAirBookingPermissions = EAirBookingPermissions;
    public showHiddenNotification: boolean;
    public warningArray: IDetailedWarning[];
    public cbRequestInError: IAirCreateBookingRequest;
    public cbNewRequestAfterPriceChanged: IAirCreateBookingRequest;
    public wording: Wording = new Wording();

    public loader: ILoader = {
        active: false,
        message: null,
        type: LoaderType.large,
    };

    public notificationNewPrice: IAirCreateBookingRequestPaymentPriceCostCurrency;
    public notificationRequestPrice: IAirCreateBookingRequestPaymentPriceCostCurrency;
    public notificationSqaRouteParams: Params;

    private link: string = null;

    private $post: IPostAirSearchRequest;
    private canViewServices: boolean;
    private canShowBookingResponse: boolean;

    //canViewServices, canShowBookingResponse

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly airBookingResourceService: AirBookingResourceService,
        private readonly bookingAirToolsService: BookingAirToolsService,
        private readonly breadcrumbIqbaService: BreadcrumbIqbaService,
        private readonly createBookingWarningMessageService: CreateBookingWarningMessageService,
        private readonly externalRedirectionService: ExternalRedirectionService,
        private readonly labelService: LabelService,
        private readonly permissionResourceService: PermissionResourceService,
        private readonly router: Router,
        private readonly tableServicesService: TableServicesService,
        private readonly bookingCbCacheService: BookingCbCacheService,
        private readonly getChangedPriceWarningPipe: GetChangedPriceWarningPipe,
        private readonly pnrService: PNRService,
        private readonly propalService: PropalService,
        private readonly priceSwitcherService: PriceSwitcherService,
    ) {
    }

    public ngOnInit(): void {
        this.priceSwitcherService.selectPriceEvent.emit(EXRateType.cost);
        this.priceSwitcherService.showPriceSwitcher(false);

        this.breadcrumbItems = this.breadcrumbIqbaService.items;
        this.breadcrumbIqbaService.setActive(2);
        this.breadcrumbIqbaService.setClickable(0, true);
        this.breadcrumbIqbaService.setClickable(1, true);
        this.breadcrumbIqbaService.setClickable(2, false);

        this.isLoading = true;
        this.isLoadingServices = false;
        this.isLoadingServicesError.active = false;

        this.initWarningMessageFromStatefulService();

        const $post: IPostAirSearchRequest = JSON.parse(sessionStorage.getItem('$post'));
        this.$post = $post;
        let canShowBooking: boolean;

        const permissionsObservable: Observable<boolean[]> = forkJoin([
            this.permissionResourceService.hasPermission(EAirBookingPermissions.SHARED_AIR_BOOKING_ANCILLARY_SERVICES_VIEW),
            this.permissionResourceService.hasPermission(EAirBookingPermissions.SHARED_AIR_BOOKING_SHOW),
        ]);

        permissionsObservable
            .subscribe(([canViewServices, canShowBookingResponse]: boolean[]): void => {
                this.canViewServices = canViewServices;
                this.canShowBookingResponse = canShowBookingResponse;

                const hasChangedPriceError: boolean = Boolean(this.getChangedPriceWarningPipe.transform(this.warningArray));

                canShowBooking = canShowBookingResponse && !hasChangedPriceError;


                this.populate(canShowBooking, canViewServices, $post);

            });
    }

    public redirectTo(): void {
        this.externalRedirectionService.redirectTo(this.link, this.bookingExternalLink.openInNewWindow);
    }

    public chooseOtherQuote(): void {
        if(this.notificationSqaRouteParams?.idSearch){
            this.router.navigate([
                IqbaPathSegment.Locobook,
                IqbaPathSegment.Search,
                this.notificationSqaRouteParams.idSearch,
                this.notificationSqaRouteParams.idQuote,
            ]);
        }else{
            this.router.navigate([IqbaPathSegment.Locobook, IqbaPathSegment.Search]);
        }
    }

    public createBookingWithNewPrice(): void {
        this.createBookingWarningMessageService.resetAll();
        this.warningMessage = this.createBookingWarningMessageService.getWarningMessage();
        this.notificationNewPrice = this.createBookingWarningMessageService.getWarningNewPrice();
        this.notificationRequestPrice = this.createBookingWarningMessageService.getWarningRequestPrice();
        this.notificationSqaRouteParams = this.createBookingWarningMessageService.getWarningSqaRouteParams();
        this.warningArray = this.createBookingWarningMessageService.getWarningArray();
        // console.log(this.cbRequestInError);
        this.loader = {
            active: true,
            message: this.wording.loaderMessage,
        };
        this.pnrService.createBooking(this.cbNewRequestAfterPriceChanged)
            .pipe(
                catchError((error: Error): Observable<{ response: IGetBookingsAirResponse, display: IAirDisplay }> => {
                    throw new RuntimeError(error.message, error);
                })
            )
            .subscribe({
                next: (booking: { request: IAirCreateBookingRequest; response: IGetBookingsAirResponse; display: IAirDisplay; }): void => {
                    console.log(booking.request)
                    this.loader.active = false;
                    this.notification.active = false;
                    this.loader.active = false;

                    this.isLoading = false;
                    this.isLoadingServices = false;
                    this.isLoadingServicesError.active = false;
                    this.createBookingWarningMessageService.setWarningMessage(booking.response.warningMessage);
                    this.createBookingWarningMessageService.setWarningNewPrice(booking.response.payment as IBookingErrorPriceChangedResponsePayment[]);
                    this.createBookingWarningMessageService.setWarningRequestPrice(booking.request);
                    if (booking.response.detailedWarningArray?.length) {
                        this.createBookingWarningMessageService.setWarningArray(booking.response.detailedWarningArray);
                        this.createBookingWarningMessageService.setCbNewRequestAfterPriceChanged(
                            booking.request,
                            booking.response as unknown as IBookingErrorPriceChangedResponse
                        );
                    }
                    this.bookingCbCacheService.storeCreateBookingResponse(booking.response.syscode, booking.response);

                    this.initWarningMessageFromStatefulService();


                    this.bookingData = booking.response;
                    const hasChangedPriceError: boolean = Boolean(this.getChangedPriceWarningPipe.transform(this.warningArray));

                    const canShowBooking: boolean = this.canShowBookingResponse && !hasChangedPriceError;

                    this.populate(canShowBooking, this.canViewServices, this.$post);

                    this.router
                        .navigateByUrl('locobook/hosts/' + booking.request.host + '/bookings/' + encodeURI(booking.response.syscode))
                        .then((): void => {
                            this.populate(canShowBooking, this.canViewServices, this.$post);
                        });
                },
                error: (error: Error): void => {
                    this.loader.active = false;
                    this.notification = {
                        active: true,
                        message: error.message,
                        type: NotificationType.Error,
                    };
                }
            });
    }

    public async updateBreadcrumb(action: {
        name: string;
    }): Promise<void> {
        switch (action.name) {
            case 'searchPage':
                this.breadcrumbIqbaService.setActive(0);
                this.breadcrumbIqbaService.setClickable(0, false);
                this.breadcrumbIqbaService.setClickable(1, true);
                this.breadcrumbIqbaService.setClickable(2, true);
                await this.router.navigate([IqbaPathSegment.Locobook, IqbaPathSegment.Search]);
                break;
            case 'travellerPage':
                this.breadcrumbIqbaService.setActive(1);
                this.breadcrumbIqbaService.setClickable(0, true);
                this.breadcrumbIqbaService.setClickable(1, false);
                this.breadcrumbIqbaService.setClickable(2, true);
                this.breadcrumbIqbaService.setShowQuotes(false);
                await this.router.navigate([IqbaPathSegment.Locobook, IqbaPathSegment.Search]);
                break;
            case 'itineraryPage':
                this.breadcrumbIqbaService.setActive(0);
                this.breadcrumbIqbaService.setClickable(0, false);
                this.breadcrumbIqbaService.setClickable(1, true);
                await this.router.navigate([IqbaPathSegment.Locobook, IqbaPathSegment.Search]);
                break;
            default:
                break;
        }
    }

    public redirect(): void {
        // Redirect
    }

    private initWarningMessageFromStatefulService(): void {
        this.warningMessage = this.createBookingWarningMessageService.getWarningMessage();
        this.notificationSqaRouteParams = this.createBookingWarningMessageService.getWarningSqaRouteParams();
        this.notificationNewPrice = this.createBookingWarningMessageService.getWarningNewPrice();
        this.notificationRequestPrice = this.createBookingWarningMessageService.getWarningRequestPrice();
        this.warningArray = this.createBookingWarningMessageService.getWarningArray();
        // this.cbRequestInError = this.createBookingWarningMessageService.getCbRequestInError();
        this.cbNewRequestAfterPriceChanged = this.createBookingWarningMessageService.getCbNewRequestAfterPriceChanged();
    }

    private configurationBookingStream(
        host: string,
        bookingSyscode: string,
        canShowBooking: boolean):
        Observable<[
            IGetBookingsAirResponse,
            IGetEnumsService[],
            ISectorServicesConfig,
        ]> {
        return forkJoin([
            // this comment is usefull to force GetBooking error for tests
            // canShowBooking ? this.airBookingResourceService.get(host, "XXXXX", wenji.ui.target): of({} as unknown as IGetBookingsAirResponse),
            canShowBooking
                ? this.airBookingResourceService.get(host, bookingSyscode, wenji.ui.target, true, null, 'merged')
                : of({} as unknown as IGetBookingsAirResponse),
            this.airBookingResourceService.getServices()
                .pipe(
                    tap((serviceEnum: IGetEnumsService[]): void => {
                        // store enum there, then it can be used if error
                        this.serviceEnum = serviceEnum;
                    }),
                ),
            // #TODO Sortir la config
            of({"air": {},})
                .pipe(
                    tap((servicesConfig: ISectorServicesConfig): void => {
                        // store config there, then it can be used if error
                        this.servicesConfig = servicesConfig;
                    })
                ),

        ]);
    }

    private generateHiddenNotification(postRequest: IPostAirSearchRequest, syscode: string): INotification {
        // #32562 no message if no booking
        if (!syscode || syscode === 'undefined') {
            return {
                active: false,
            };
        }
        return {
            active: true,
            type: NotificationType.Success,
            message: postRequest && postRequest.externalContextId ?
                this.labelService.completeLabel(
                    Yii.t("wui-iqba-js", "Le dossier %syscode%, de la référence : %externalContextId% existe"),
                    {
                        syscode,
                        externalContextId: postRequest.externalContextId,
                    }
                ) :
                this.labelService.completeLabel(Yii.t("wui-iqba-js", "Le dossier %placeholder% existe"), syscode),
        };
    }

    private populate(canShowBooking: boolean, canViewServices: boolean, $post: IPostAirSearchRequest): void {
        let paramsHost: string;
        let paramsBookingSyscode: string;
        this.activatedRoute.params
            .pipe(
                mergeMap((parameters: Params): Observable<[
                    IGetBookingsAirResponse,
                    IGetEnumsService[],
                    ISectorServicesConfig,
                ]> => {
                    this.breadcrumbIqbaService.setPnrPageParameters(parameters.host, parameters.booking);
                    paramsHost = parameters.host;
                    paramsBookingSyscode = parameters.booking;
                    return this.configurationBookingStream(parameters.host, parameters.booking, canShowBooking);
                }),
                tap(([bookingResponse, enums, servicesConfig]: [
                    IGetBookingsAirResponse,
                    IGetEnumsService[],
                    ISectorServicesConfig,
                ]): void => {
                    if (canShowBooking && this.bookingAirToolsService.hasServices(bookingResponse) && canViewServices) {
                        this.isLoadingServices = true;
                        this.isLoadingServicesError.active = false;

                        this.airBookingResourceService.getWithServiceSelection(paramsHost, paramsBookingSyscode, wenji.ui.target, true)
                            .subscribe({
                                // tslint:disable-next-line:no-any
                                next: (bookingWithSelection: any): void => {
                                    this.isLoadingServices = false;
                                    this.bookingWithSelection = bookingWithSelection;
                                },
                                error: (err: Error): void => {
                                    this.isLoadingServices = false;
                                    this.isLoadingServicesError.active = true;
                                    this.isLoadingServicesError.message = Yii.t('wui-sasha-js', 'Erreur lors de la récupération des services : ') + err.message;
                                    console.error(err);
                                }

                            });
                    }
                }),
            ).subscribe({
            next: ([bookingResponse, enums, servicesConfig]: [
                IGetBookingsAirResponse,
                IGetEnumsService[],
                ISectorServicesConfig,
            ]): void => {
                if (canShowBooking) {
                    if ($post?.bookingExternalLink?.link) {
                        this.bookingExternalLink = $post.bookingExternalLink;

                        const link: string = $post.bookingExternalLink.link;
                        const hasNoAddBookingArgsToURLProperty: boolean = typeof $post.bookingExternalLink.addBookingArgsToURL === "undefined";

                        const addBookingArgsToURL: boolean = hasNoAddBookingArgsToURLProperty || $post.bookingExternalLink.addBookingArgsToURL;

                        this.link = !addBookingArgsToURL ?
                            this.externalRedirectionService.generateUrl(link) :
                            this.externalRedirectionService.generateUrl(link, bookingResponse.host, bookingResponse.code, bookingResponse.whenCreated);

                        if ($post.bookingExternalLink.autoRedirect) {
                            this.redirectTo();
                        }
                    }

                    this.isLoading = false;
                    this.bookingData = bookingResponse;
                    this.hiddenBookingNotification = this.generateHiddenNotification($post, this.bookingData.syscode);
                    this.tableServices = this.tableServicesService.getBookingTableServicesMapping(bookingResponse, this.servicesConfig, this.serviceEnum);
                } else {
                    this.isLoading = false;
                    this.bookingData = null;
                    this.hiddenBookingNotification = this.generateHiddenNotification($post, paramsBookingSyscode);
                }
            },
            error: (error: Error): void => {
                console.log({error});
                this.isLoading = false;
                this.bookingData = null;
                //if the user can't show the booking, use hiddenBookinNotification
                if (!canShowBooking) {
                    this.hiddenBookingNotification = this.generateHiddenNotification($post, paramsBookingSyscode);
                }
                //if error and permission to show booking, but has cached response, use it
                if (this.bookingCbCacheService.hasCreateBookingResponse(paramsBookingSyscode)) {
                    this.bookingData = this.bookingCbCacheService.getCreateBookingResponse(paramsBookingSyscode);

                    this.tableServices = this.tableServicesService.getBookingTableServicesMapping(this.bookingData, this.servicesConfig, this.serviceEnum);

                    this.hiddenBookingNotification.active = false;
                    this.notification.active = false;
                } else {
                    // if error, permission to show booking but no cache, show notification that booking exist
                    this.notification = this.generateHiddenNotification($post, paramsBookingSyscode);
                    this.hiddenBookingNotification.active = false;
                }
            }
        });
    }
}
